import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Paper,
  Box,
  Chip,
  Alert,
  Snackbar,
  Fade,
  ThemeProvider,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  IconButton,
  CircularProgress
} from '@mui/material';
import {
  VideoLibrary,
  AccountCircle,
  CheckCircle,
  CheckCircleOutline,
  Close,
  Compress
} from '@mui/icons-material';
import { BrowserRouter as Router, Route, Routes, Navigate, useNavigate } from 'react-router-dom';

import AuthCallback from './AuthCallback';
import AddCreditsPage from './components/AddCreditsPage';
import SuccessPage from './components/SuccessPage';
import { ThemeProvider as CustomThemeProvider, useTheme } from './contexts/ThemeContext';
import { getTheme } from './theme/themes';
import ThemeSelector from './components/ThemeSelector';
import GoogleSignInButton from './components/GoogleSignInButton';
import GooglePhotosButton from './components/GooglePhotosButton';
import GoogleOneTap from './components/GoogleOneTap';
import CompressionStatusIndicator, { CompressionStatus } from './components/CompressionStatusIndicator';
import ThemeAwareLogo from './components/ThemeAwareLogo';
import OperationsSidebar from './components/OperationsSidebar';
import JobsTray from './components/JobsTray';
import OperationsTray from './components/OperationsTray';
import MobileSelectionControls from './components/MobileSelectionControls';
import { JobData } from './components/JobsPanel';
import GallerySortControls from './components/GallerySortControls';
import SplashPage from './components/SplashPage';
import AccountDropdown from './components/AccountDropdown';
import AccountScreen from './components/AccountScreen';
import Footer from './components/Footer';
import PrivacyPolicy from './components/PrivacyPolicy';
import TermsAndConditions from './components/TermsAndConditions';
import CookiePolicy from './components/CookiePolicy';
import { db } from './firebase';
import { Timestamp, collection, query, where, onSnapshot } from "firebase/firestore";
import {
  showJobCompletionNotification,
  showBatchCompletionNotification,
  shouldShowNotifications
} from './utils/notifications';
import authService from './services/authService';
import { User } from 'firebase/auth';

// Component that uses navigation - must be inside Router
const AppWithNavigation: React.FC = () => {
  const { effectiveTheme } = useTheme();
  const theme = getTheme(effectiveTheme);
  const navigate = useNavigate();

  interface MediaItem {
    id: string;
    filename: string;
    mimeType: string;
    baseUrl: string;
    googlePhotosUrl?: string;
    fileSizeBytes?: number;
    addedAt?: string; // ISO timestamp when the media item was added
    mediaMetadata?: {
      width: string;
      height: string;
      creationTime: string;
      video?: {
        fps: number;
        status: string;
      };
    };
  }

  // Utility function to format file sizes
  const formatFileSize = (bytes?: number): string => {
    if (!bytes) return '';

    const units = ['B', 'KB', 'MB', 'GB'];
    let size = bytes;
    let unitIndex = 0;

    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024;
      unitIndex++;
    }

    return `${size.toFixed(unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
  };

  // Function to sort media items based on the selected sort option
  const getSortedMediaItems = useCallback((items: MediaItem[], sortBy: 'size' | 'date'): MediaItem[] => {
    return [...items].sort((a, b) => {
      if (sortBy === 'size') {
        const sizeA = a.fileSizeBytes || 0;
        const sizeB = b.fileSizeBytes || 0;
        return sizeB - sizeA; // Largest first
      }

      if (sortBy === 'date') {
        const dateA = a.mediaMetadata?.creationTime ? new Date(a.mediaMetadata.creationTime).getTime() : 0;
        const dateB = b.mediaMetadata?.creationTime ? new Date(b.mediaMetadata.creationTime).getTime() : 0;
        return dateB - dateA; // Newest first
      }

      return 0;
    });
  }, []);

  // Utility function to estimate file size based on dimensions and media type
  const estimateFileSize = (mediaItem: MediaItem): number => {
    const width = mediaItem.mediaMetadata?.width ? parseInt(mediaItem.mediaMetadata.width) : 1920;
    const height = mediaItem.mediaMetadata?.height ? parseInt(mediaItem.mediaMetadata.height) : 1080;
    const pixels = width * height;

    if (mediaItem.mimeType?.startsWith('video/')) {
      // Video size estimation: assume 30fps, 60 seconds, moderate compression
      const fps = mediaItem.mediaMetadata?.video?.fps || 30;
      const durationSeconds = 60; // Default estimate
      const bitsPerPixel = 0.1; // Moderate compression
      return Math.round(pixels * fps * durationSeconds * bitsPerPixel / 8);
    } else {
      // Photo size estimation: assume JPEG compression
      const bitsPerPixel = 2; // JPEG compression ratio
      return Math.round(pixels * bitsPerPixel / 8);
    }
  };

  // Helper function to get initial UI state from localStorage
  const getInitialBooleanState = (key: string, defaultValue: boolean): boolean => {
    try {
      const stored = localStorage.getItem(key);
      if (stored !== null) {
        return stored === 'true';
      }
    } catch (error) {
      console.warn(`Failed to read ${key} from localStorage:`, error);
    }
    return defaultValue;
  };

  const [user, setUser] = useState<User | null>(null);
  const [idToken, setIdToken] = useState<string | null>(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [mediaItems, setMediaItems] = useState<MediaItem[]>([]);
  const [notification, setNotification] = useState<string | null>(null);
  const [notificationType, setNotificationType] = useState<'success' | 'error' | 'info' | 'warning'>('info');
  const [videosLoading, setVideosLoading] = useState(false);
  const [, setCancelPolling] = useState(false);
  const cancelPollingRef = useRef(false);
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [lastSelectedIndex, setLastSelectedIndex] = useState<number | null>(null);
  const [compressionModalOpen, setCompressionModalOpen] = useState(false);
  const [sortBy, setSortBy] = useState<'size' | 'date'>('size');
  const [compressionTarget, setCompressionTarget] = useState<MediaItem | 'batch' | null>(null);
  const [compressionSettings, setCompressionSettings] = useState({
    quality: 'medium',
    uploadToGooglePhotos: true
  });
  const [creditEstimate, setCreditEstimate] = useState<{
    estimatedCredits: number;
    description: string;
    totalCredits?: number;
  } | null>(null);
  const [userCredits, setUserCredits] = useState<number>(0);
  const [creditsLoading, setCreditsLoading] = useState<boolean>(true);
  const [userProfile, setUserProfile] = useState<{ email: string, firstName?: string, profilePictureUrl?: string } | null>(null);
  const [notificationsEnabled, setNotificationsEnabled] = useState(true);
  const [compressionStatuses, setCompressionStatuses] = useState<Map<string, CompressionStatus>>(new Map());
  const [expiredMediaItems, setExpiredMediaItems] = useState<Set<string>>(new Set());

  // UI state with localStorage persistence
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => getInitialBooleanState('operations-sidebar-collapsed', false));
  const [showInstructions, setShowInstructions] = useState(() => getInitialBooleanState('show-instructions', true));
  const [loadingPastMedia, setLoadingPastMedia] = useState(false);
  const [jobsPanelCollapsed, setJobsPanelCollapsed] = useState(() => getInitialBooleanState('jobs-panel-collapsed', true));
  const [operationsTrayCollapsed, setOperationsTrayCollapsed] = useState(() => getInitialBooleanState('operations-tray-collapsed', true));
  const [jobs, setJobs] = useState<JobData[]>([]);

  // Listen for Firebase auth state changes
  useEffect(() => {
    const unsubscribe = authService.onAuthStateChange(async (firebaseUser) => {
      setUser(firebaseUser);

      if (firebaseUser) {
        // Get ID token for API requests
        try {
          const token = await authService.getIdToken();
          setIdToken(token);
        } catch (error) {
          console.error('Error getting ID token:', error);
          setIdToken(null);
        }
      } else {
        setIdToken(null);
      }

      // Auth state has been determined
      setAuthLoading(false);
    });

    return unsubscribe;
  }, []);

  // Load media items from database when component mounts or user changes
  useEffect(() => {
    if (user && idToken) {
      loadMediaItemsFromDatabase();
      fetchUserCredits(); // Load user credits
      fetchUserProfile(); // Load user profile
    }
  }, [user, idToken]); // eslint-disable-line react-hooks/exhaustive-deps

  // ... inside your React component or hook

  useEffect(() => {
    // 1. Create a reference to the collection.
    const jobsCollectionRef = collection(db, "compressionJobs");

    const userId = getCurrentUserId();
    if (!userId) {
      setJobs([]);
      return;
    }

    // 2. Create the query by combining the reference and the 'where' constraint.
    const q = query(jobsCollectionRef, where("userId", "==", userId));

    // 3. Attach the listener to the query object.
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const updatedJobs: JobData[] = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        const job: JobData = {
          jobId: doc.id,
          mediaItemId: data.mediaItemId,
          filename: data.filename,
          mimeType: data.mimeType,
          baseUrl: data.baseUrl,
          status: data.status,
          message: data.message || getStatusMessage(data.status),
          progress: data.progress || getProgressPercentage(data.status),
          quality: data.quality,
          uploadToGooglePhotos: data.uploadToGooglePhotos,
          createdAt: (data.createdAt as Timestamp)?.toDate().toISOString() || new Date().toISOString(),
          completedAt: (data.completedAt as Timestamp)?.toDate().toISOString(),
          compressionRatio: data.compressionRatio,
          error: data.errorMessage,
          mediaType: data.mediaType === 1 ? 'Video' : 'Photo',
          googlePhotosUrl: data.googlePhotosUrl,
          compressedGooglePhotosUrl: data.compressedGooglePhotosUrl,
          creditsUsed: data.creditsUsed,
          previewImagePath: data.previewImagePath,
        };
        updatedJobs.push(job);
      });
      console.log("Firestore snapshot received, jobs updated:", updatedJobs);
      setJobs(updatedJobs);

      // Update compression statuses for gallery indicators
      setCompressionStatuses(prev => {
        const newMap = new Map(prev);
        updatedJobs.forEach(job => {
          if (job.mediaItemId) {
            newMap.set(job.mediaItemId, {
              jobId: job.jobId,
              status: job.status,
              message: job.message,
              progress: job.progress,
              error: job.error
            });
          }
        });
        return newMap;
      });
    }, (error) => {
      console.error("Error with Firestore snapshot listener:", error);
      showNotification("Error connecting to real-time updates.", "error");
    });

    // Cleanup listener on component unmount (this part was already correct)
    return () => {
      console.log("Cleaning up Firestore listener");
      unsubscribe();
    };
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  // Track job completions for notifications with debouncing
  const previousJobsRef = useRef<JobData[]>([]);
  const notificationQueueRef = useRef<JobData[]>([]);
  const notificationTimerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (jobs.length === 0) {
      previousJobsRef.current = [];
      return;
    }

    const previousJobs = previousJobsRef.current;

    // Find newly completed jobs in this update
    const newlyCompletedJobs = jobs.filter(job => {
      const wasCompleted = previousJobs.find(prevJob =>
        prevJob.jobId === job.jobId &&
        (prevJob.status.toLowerCase() === 'completed' || prevJob.status.toLowerCase() === 'failed')
      );
      const isNowCompleted = job.status.toLowerCase() === 'completed' || job.status.toLowerCase() === 'failed';
      return !wasCompleted && isNowCompleted;
    });

    // Queue them up
    if (newlyCompletedJobs.length > 0 && shouldShowNotifications(notificationsEnabled)) {
      notificationQueueRef.current = [...notificationQueueRef.current, ...newlyCompletedJobs];

      // Reset timer (debounce)
      if (notificationTimerRef.current) {
        clearTimeout(notificationTimerRef.current);
      }

      notificationTimerRef.current = setTimeout(() => {
        const jobsToNotify = notificationQueueRef.current;
        notificationQueueRef.current = []; // Clear queue

        if (jobsToNotify.length === 0) return;

        if (jobsToNotify.length === 1) {
          const job = jobsToNotify[0];
          const success = job.status.toLowerCase() === 'completed';
          const filename = job.filename || 'Unknown file';

          showJobCompletionNotification(filename, success, () => {
            setJobsPanelCollapsed(false);
          });
        } else {
          // Show batched notification
          const successCount = jobsToNotify.filter(j => j.status.toLowerCase() === 'completed').length;
          const failCount = jobsToNotify.length - successCount;

          showBatchCompletionNotification(
            jobsToNotify.length,
            successCount,
            failCount,
            () => {
              setJobsPanelCollapsed(false);
            }
          );
        }
      }, 1000); // Wait 1 second for other jobs to potentially complete
    }

    previousJobsRef.current = [...jobs];

    // Cleanup timer on unmount
    return () => {
      if (notificationTimerRef.current) {
        // Don't clear timeout here strictly, or we might miss the final notification on unmount 
        // but typically unmount means user left, so it's fine.
      }
    };
  }, [jobs, notificationsEnabled, setJobsPanelCollapsed]);

  // Re-estimate costs when quality changes in the modal
  useEffect(() => {
    if (compressionModalOpen && compressionTarget) {
      setCreditEstimate(null); // Reset to show loading state
      estimateCompressionCost(compressionTarget, compressionSettings.quality);
    }
  }, [compressionSettings.quality, compressionModalOpen, compressionTarget]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-open jobs panel when jobs first appear (but allow manual minimization)
  // Only auto-open on desktop, not mobile
  const isMobile = useMediaQuery('(max-width:959px)'); // md breakpoint is 960px
  const [hasSeenJobs, setHasSeenJobs] = useState(false);
  useEffect(() => {
    if (jobs.length > 0 && !hasSeenJobs && jobsPanelCollapsed && !isMobile) {
      console.log("Auto-opening jobs panel - found", jobs.length, "jobs (desktop only)");
      setJobsPanelCollapsed(false);
      setHasSeenJobs(true);
    } else if (jobs.length === 0) {
      // Reset when no jobs remain
      setHasSeenJobs(false);
    }
  }, [jobs.length, hasSeenJobs, jobsPanelCollapsed, isMobile]); // eslint-disable-line react-hooks/exhaustive-deps

  // Save UI state to localStorage when it changes
  useEffect(() => {
    try {
      localStorage.setItem('operations-sidebar-collapsed', sidebarCollapsed.toString());
    } catch (error) {
      console.warn('Failed to save operations-sidebar-collapsed to localStorage:', error);
    }
  }, [sidebarCollapsed]);

  useEffect(() => {
    try {
      localStorage.setItem('show-instructions', showInstructions.toString());
    } catch (error) {
      console.warn('Failed to save show-instructions to localStorage:', error);
    }
  }, [showInstructions]);

  useEffect(() => {
    try {
      localStorage.setItem('jobs-panel-collapsed', jobsPanelCollapsed.toString());
    } catch (error) {
      console.warn('Failed to save jobs-panel-collapsed to localStorage:', error);
    }
  }, [jobsPanelCollapsed]);

  // Helper function to check if a media item has expired (older than 1 hour)
  const isMediaItemExpired = (mediaItem: MediaItem): boolean => {
    if (!mediaItem.addedAt) return false;

    const addedAtDate = new Date(mediaItem.addedAt);
    const now = new Date();
    const ageInHours = (now.getTime() - addedAtDate.getTime()) / (1000 * 60 * 60);

    return ageInHours > 1;
  };

  // Helper function to handle expired media items
  const handleMediaExpired = (mediaItemId: string) => {
    setExpiredMediaItems(prev => new Set(Array.from(prev).concat(mediaItemId)));
  };

  // Helper function to remove expired media items
  const removeExpiredMediaItems = async (mediaItemIds: string[]) => {
    if (!idToken) return;

    try {
      // Optimized: Remove from database in batch using standardized endpoint
      const response = await fetch('/api/usermediaitems/batch?idType=google', {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(mediaItemIds)
      });

      if (!response.ok) {
        console.warn(`Failed to batch delete media items: ${response.status}`);
        // We continue anyway to clean up local state
      }

      // Remove from local state
      setMediaItems(prev => prev.filter(item => !mediaItemIds.includes(item.id)));
      setExpiredMediaItems(prev => {
        const newSet = new Set(prev);
        mediaItemIds.forEach(id => newSet.delete(id));
        return newSet;
      });
      setSelectedItems(prev => {
        const newSet = new Set(prev);
        mediaItemIds.forEach(id => newSet.delete(id));
        return newSet;
      });

      showNotification(`Removed ${mediaItemIds.length} expired media item${mediaItemIds.length > 1 ? 's' : ''}`, 'success');
    } catch (error) {
      console.error('Error removing expired media items:', error);
      showNotification('Failed to remove expired media items', 'error');
    }
  };

  // Helper function to get status message
  const getStatusMessage = (status: string): string => {
    switch (status) {
      case 'Queued': return 'Queued for processing';
      case 'DownloadingFromGooglePhotos': return 'Downloading from Google Photos';
      case 'UploadingToStorage': return 'Uploading to cloud storage';
      case 'TranscodingInProgress': return 'Video transcoding in progress';
      case 'CompressingImage': return 'Compressing image';
      case 'DownloadingFromStorage': return 'Downloading compressed media';
      case 'ReadyForBatchUpload': return 'Ready for upload';
      case 'UploadingToGooglePhotos': return 'Uploading to Google Photos';
      case 'Completed': return 'Compression completed';
      case 'Failed': return 'Compression failed';
      case 'Cancelled': return 'Compression cancelled';
      // Legacy statuses for backward compatibility
      case 'Processing': return 'Processing...';
      case 'Uploading': return 'Uploading to Google Photos...';
      case 'UploadCompleted': return 'Upload completed';
      case 'UploadFailed': return 'Upload failed';
      default: return status;
    }
  };

  // Helper function to convert enum number to status string (for backward compatibility)
  const getStatusFromEnum = (statusEnum: number): string => {
    const statusMap: { [key: number]: string } = {
      0: 'Queued',
      1: 'DownloadingFromGooglePhotos',
      2: 'UploadingToStorage',
      3: 'TranscodingInProgress',
      4: 'CompressingImage',
      5: 'DownloadingFromStorage',
      6: 'ReadyForBatchUpload',
      7: 'UploadingToGooglePhotos',
      8: 'Completed',
      9: 'Failed',
      10: 'Cancelled'
    };
    return statusMap[statusEnum] || 'Unknown';
  };

  // Helper function to get progress percentage based on status
  const getProgressPercentage = (status: string): number => {
    switch (status) {
      case 'Queued': return 0;
      case 'DownloadingFromGooglePhotos': return 15;
      case 'UploadingToStorage': return 25;
      case 'TranscodingInProgress': return 50;
      case 'CompressingImage': return 50;
      case 'DownloadingFromStorage': return 70;
      case 'ReadyForBatchUpload': return 80;
      case 'UploadingToGooglePhotos': return 90;
      case 'Completed': return 100;
      case 'Failed': return 0;
      case 'Cancelled': return 0;
      // Legacy statuses
      case 'Processing': return 50;
      case 'Uploading': return 90;
      case 'UploadCompleted': return 100;
      case 'UploadFailed': return 0;
      default: return 0;
    }
  };

  // Helper function for showing notifications
  const showNotification = (message: string, type: 'success' | 'error' | 'info' | 'warning' = 'info') => {
    setNotification(message);
    setNotificationType(type);
  };

  // Helper function to get current user ID from Firebase Auth
  const getCurrentUserId = () => {
    return user?.uid || null;
  };

  // Helper function to get current user email from user profile or Firebase Auth
  const getCurrentUserEmail = () => {
    if (userProfile?.email) return userProfile.email;
    if (user?.email) return user.email;
    return '';
  };



  // Selection handling functions
  const handleItemClick = (mediaItem: MediaItem, index: number, event: React.MouseEvent) => {
    // Prevent default browser selection behavior
    event.preventDefault();

    const itemId = mediaItem.id;

    if (event.shiftKey && lastSelectedIndex !== null) {
      // Shift-click: select range
      const start = Math.min(lastSelectedIndex, index);
      const end = Math.max(lastSelectedIndex, index);
      const newSelected = new Set(selectedItems);

      for (let i = start; i <= end; i++) {
        if (sortedMediaItems[i]?.id) {
          newSelected.add(sortedMediaItems[i].id);
        }
      }

      setSelectedItems(newSelected);
    } else if (event.ctrlKey || event.metaKey || isMobile) {
      // Ctrl/Cmd-click on desktop OR regular click on mobile: toggle individual item
      const newSelected = new Set(selectedItems);
      if (newSelected.has(itemId)) {
        newSelected.delete(itemId);
      } else {
        newSelected.add(itemId);
      }
      setSelectedItems(newSelected);
      setLastSelectedIndex(index);
    } else {
      // Regular click on desktop: handle selection/deselection or open in Google Photos
      const newSelected = new Set(selectedItems);

      if (selectedItems.has(itemId) && selectedItems.size === 1) {
        // If this is the only selected item, open in Google Photos if URL is available
        if (mediaItem.googlePhotosUrl) {
          window.open(mediaItem.googlePhotosUrl, '_blank');
        } else {
          // If no Google Photos URL, deselect the item
          newSelected.clear();
          setLastSelectedIndex(null);
          setSelectedItems(newSelected);
        }
      } else {
        // Otherwise, select only this item
        newSelected.clear();
        newSelected.add(itemId);
        setLastSelectedIndex(index);
        setSelectedItems(newSelected);
      }
    }
  };

  // Memoize sorted items to avoid recalculating on every render
  const sortedMediaItems = useMemo(() => getSortedMediaItems(mediaItems, sortBy), [mediaItems, sortBy, getSortedMediaItems]);

  const handleSelectAll = () => {
    const allIds = sortedMediaItems.map(item => item.id).filter(Boolean);
    setSelectedItems(new Set(allIds));
  };

  const handleClearSelection = () => {
    setSelectedItems(new Set());
    setLastSelectedIndex(null);
  };

  const handleClearAllExpired = () => {
    const expiredIds = Array.from(expiredMediaItems);
    removeExpiredMediaItems(expiredIds);
  };



  // Make authentication handler available globally for AuthCallback

  const handleGoogleSignIn = async () => {
    // Instead of using ID token, redirect to OAuth flow to get proper tokens
    try {
      const response = await fetch('/api/auth/google-oauth-url');
      const data = await response.json();
      window.location.href = data.authUrl;
    } catch (err) {
      console.error('Error getting OAuth URL:', err);
      showNotification('Error starting Google authentication', 'error');
    }
  };

  const handleOneTapSuccess = async (customToken: string) => {
    try {
      console.log('One Tap authentication successful, signing in with Firebase...');
      await authService.signInWithCustomToken(customToken);
      showNotification('Successfully signed in with Google!', 'success');
      navigate('/');
    } catch (error) {
      console.error('Error signing in with Firebase:', error);
      showNotification('Authentication failed', 'error');
    }
  };

  const handleOneTapError = (error: any) => {
    console.error('One Tap authentication error:', error);
    // Don't show error notifications for One Tap since it's meant to be unobtrusive
    // Users can still use the regular sign-in button if One Tap fails
  };

  // Make authentication handler available globally for AuthCallback
  useEffect(() => {
    (window as any).handleAuthCallback = handleOneTapSuccess;
    return () => {
      delete (window as any).handleAuthCallback;
    };
  }, [handleOneTapSuccess]);



  const handleLogout = async () => {
    try {
      await authService.signOut();
      setMediaItems([]);
      showNotification('Successfully signed out', 'info');
    } catch (error) {
      console.error('Error signing out:', error);
      showNotification('Error signing out', 'error');
    }
  };

  const fetchUserCredits = async () => {
    if (!idToken) return;

    try {
      setCreditsLoading(true);
      const response = await fetch('/api/credits/balance', {
        headers: {
          'Authorization': `Bearer ${idToken}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setUserCredits(data.credits);
      }
    } catch (error) {
      console.error('Error fetching user credits:', error);
    } finally {
      setCreditsLoading(false);
    }
  };

  const fetchUserProfile = async () => {
    if (!idToken) return;

    try {
      const response = await fetch('/api/auth/me', {
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setUserProfile({
          email: data.email,
          firstName: data.firstName,
          profilePictureUrl: data.profilePictureUrl
        });
        setNotificationsEnabled(data.notificationsEnabled ?? true);
      } else {
        console.error('Failed to fetch user profile');
      }
    } catch (error) {
      console.error('Error fetching user profile:', error);
    }
  };

  const updateUserPreferences = async (preferences: { notificationsEnabled?: boolean }) => {
    if (!idToken) return;

    try {
      const response = await fetch('/api/userpreferences', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(preferences)
      });

      if (response.ok) {
        const data = await response.json();
        setNotificationsEnabled(data.notificationsEnabled);
      } else {
        console.error('Failed to update user preferences');
        showNotification('Failed to update preferences', 'error');
      }
    } catch (error) {
      console.error('Error updating user preferences:', error);
      showNotification('Failed to update preferences', 'error');
    }
  };

  const handleNotificationsChange = (enabled: boolean) => {
    updateUserPreferences({ notificationsEnabled: enabled });
  };

  const estimateCompressionCost = async (mediaItem: MediaItem | 'batch', quality: string) => {
    if (!idToken) return;

    try {
      if (mediaItem === 'batch') {
        // For batch operations, calculate total cost for all selected items using batch endpoint
        const selectedMediaItems = mediaItems.filter(item => selectedItems.has(item.id));

        const batchItems = selectedMediaItems.map(item => {
          const isVideo = item.mimeType?.startsWith('video/') ?? false;
          const mediaType = isVideo ? 1 : 0; // Photo = 0, Video = 1
          return {
            mediaType,
            quality,
            durationMinutes: isVideo ? 1 : null, // Default to 1 minute
            fileSizeBytes: item.fileSizeBytes
          };
        });

        const response = await fetch('/api/credits/batch/estimate', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${idToken}`
          },
          body: JSON.stringify({ items: batchItems })
        });

        if (response.ok) {
          const data = await response.json();
          setCreditEstimate({
            estimatedCredits: data.totalEstimatedCredits,
            description: data.description,
            totalCredits: data.totalEstimatedCredits
          });
        }
      } else {
        // Single item estimation
        const isVideo = mediaItem.mimeType?.startsWith('video/') ?? false;
        const mediaType = isVideo ? 1 : 0; // Photo = 0, Video = 1

        const response = await fetch('/api/credits/estimate', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${idToken}`
          },
          body: JSON.stringify({
            mediaType,
            quality,
            durationMinutes: isVideo ? 1 : null, // Default to 1 minute for videos since duration isn't available in frontend
            fileSizeBytes: mediaItem.fileSizeBytes
          })
        });

        if (response.ok) {
          const data = await response.json();
          setCreditEstimate({
            estimatedCredits: data.estimatedCredits,
            description: data.description
          });
        }
      }
    } catch (error) {
      console.error('Error estimating compression cost:', error);
    }
  };



  const handleBatchCompress = () => {
    if (selectedItems.size === 0) {
      showNotification('No items selected for compression', 'error');
      return;
    }

    setCompressionTarget('batch');
    setCreditEstimate(null); // Reset credit estimate
    setCompressionModalOpen(true);
    fetchUserCredits();
    // Removed explicit estimateCompressionCost call to avoid double request
    // The useEffect hook watching compressionModalOpen/compressionTarget will handle this
  };

  const executeCompression = async (mediaItem: MediaItem, settings: typeof compressionSettings) => {
    try {
      // Determine media type based on MIME type
      const isVideo = mediaItem.mimeType?.startsWith('video/') ?? false;
      const mediaType = isVideo ? 1 : 0; // Photo = 0, Video = 1 (enum values)

      const response = await fetch(`/api/media/${mediaItem.id}/compress`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          quality: settings.quality,
          mediaType: mediaType,
          uploadToGooglePhotos: settings.uploadToGooglePhotos,
          filename: mediaItem.filename,
          baseUrl: mediaItem.baseUrl,
          originalWidth: mediaItem.mediaMetadata?.width ? parseInt(mediaItem.mediaMetadata.width) : null,
          originalHeight: mediaItem.mediaMetadata?.height ? parseInt(mediaItem.mediaMetadata.height) : null,
          googlePhotosUrl: mediaItem.googlePhotosUrl,
          originalCreationTime: mediaItem.mediaMetadata?.creationTime ? new Date(mediaItem.mediaMetadata.creationTime).toISOString() : null
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to start compression: ${response.status}`);
      }

      const data = await response.json();
      console.log('Compression job queued:', data);

      // Immediately set the initial status
      setCompressionStatuses(prev => {
        const newMap = new Map(prev);
        newMap.set(mediaItem.id, {
          jobId: data.jobId,
          status: 'Queued',
          message: 'Queued for processing',
          progress: 0
        });
        return newMap;
      });

      // Auto-open jobs panel when compression starts (only if user hasn't seen jobs yet and on desktop)
      if (!hasSeenJobs && !isMobile) {
        setJobsPanelCollapsed(false);
        setHasSeenJobs(true);
      }

      return { success: true, jobId: data.jobId };
    } catch (err) {
      console.error('Error starting compression:', err);
      return { success: false, error: err };
    }
  };

  const handleConfirmCompression = async () => {
    setCompressionModalOpen(false);

    if (compressionTarget === 'batch') {
      const selectedMediaItems = mediaItems.filter(item => selectedItems.has(item.id));

      showNotification(`Starting compression for ${selectedItems.size} items...`, 'info');

      // Clear selection immediately when compression starts
      handleClearSelection();

      try {
        // Construct batch request payload
        const batchItems = selectedMediaItems.map(item => {
          const isVideo = item.mimeType?.startsWith('video/') ?? false;
          const mediaType = isVideo ? 1 : 0;
          return {
            mediaItemId: item.id, // Required by our wrapper
            quality: compressionSettings.quality,
            mediaType: mediaType,
            uploadToGooglePhotos: compressionSettings.uploadToGooglePhotos,
            filename: item.filename,
            baseUrl: item.baseUrl,
            originalWidth: item.mediaMetadata?.width ? parseInt(item.mediaMetadata.width) : null,
            originalHeight: item.mediaMetadata?.height ? parseInt(item.mediaMetadata.height) : null,
            googlePhotosUrl: item.googlePhotosUrl,
            originalCreationTime: item.mediaMetadata?.creationTime
          };
        });

        const response = await fetch('/api/media/batch/compress', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${idToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ items: batchItems })
        });

        if (response.ok) {
          const data = await response.json();

          // Update local state with queued jobs
          setJobs(prev => {
            const newJobs = [...prev];
            // We can't easily map the response results back to full job objects without fetching, 
            // but the real-time listener will pick them up shortly.
            // For immediate feedback, we can optimistically add them if the response contains enough info,
            // or just rely on the listener. 
            // The batch response has job IDs.
            return newJobs;
          });

          if (data.failedItems > 0) {
            showNotification(`Batch started: ${data.successfulItems} queued, ${data.failedItems} failed.`, 'warning');
          } else {
            showNotification(`Successfully queued ${data.successfulItems} items.`, 'success');
          }

          // Auto-open jobs panel
          if (!hasSeenJobs && !isMobile) {
            setJobsPanelCollapsed(false);
            setHasSeenJobs(true);
          }

        } else {
          const error = await response.json();
          showNotification(error.error || 'Batch compression failed', 'error');
        }

      } catch (error) {
        console.error('Unexpected error during batch compression:', error);
        showNotification('Unexpected error during batch compression', 'error');
      }
    } else if (compressionTarget) {
      const result = await executeCompression(compressionTarget, compressionSettings);
      if (result.success) {
        showNotification(`Compression started for ${compressionTarget.filename}`, 'success');
      } else {
        showNotification('Error starting compression', 'error');
      }
    }

    setCompressionTarget(null);
  };

  const handleCancelCompression = () => {
    setCompressionModalOpen(false);
    setCompressionTarget(null);
  };

  const loadMediaItemsFromDatabase = async () => {
    if (!idToken) {
      return;
    }

    setLoadingPastMedia(true);
    try {
      const response = await fetch('/api/usermediaitems', {
        headers: {
          'Authorization': `Bearer ${idToken}`
        }
      });

      if (!response.ok) {
        if (response.status === 401) {
          // User not authenticated, ignore
          return;
        }
        throw new Error(`Failed to load media items: ${response.status}`);
      }

      const savedMediaItems = await response.json();
      console.log('Loaded media items from database:', savedMediaItems);

      // Convert saved media items back to the MediaItem format expected by the UI
      const convertedMediaItems: MediaItem[] = savedMediaItems.map((item: any) => ({
        id: item.googleMediaItemId,
        filename: item.filename,
        mimeType: item.mimeType,
        baseUrl: item.baseUrl,
        fileSizeBytes: item.fileSizeBytes,
        addedAt: item.addedAt, // Include timestamp for expiration checking
        mediaMetadata: item.metadata ? JSON.parse(item.metadata) : {
          width: item.width?.toString() || '',
          height: item.height?.toString() || '',
          creationTime: item.creationTime || ''
        }
      }));

      // Mark expired media items
      const expiredIds = convertedMediaItems
        .filter(item => isMediaItemExpired(item))
        .map(item => item.id);

      if (expiredIds.length > 0) {
        console.log(`Found ${expiredIds.length} expired media items`);
        setExpiredMediaItems(new Set(expiredIds));
      }

      // Update compression statuses from the loaded data
      const newCompressionStatuses = new Map<string, CompressionStatus>();
      savedMediaItems.forEach((item: any) => {
        if (item.latestCompressionJob) {
          const job = item.latestCompressionJob;
          // Ensure status is a string (handle both string and numeric enum values)
          const statusString = typeof job.status === 'string' ? job.status : getStatusFromEnum(job.status);

          newCompressionStatuses.set(item.googleMediaItemId, {
            jobId: job.id,
            status: statusString,
            message: job.errorMessage || getStatusMessage(statusString),
            progress: getProgressPercentage(statusString),
            error: job.errorMessage
          });
        }
      });

      setCompressionStatuses(newCompressionStatuses);
      setMediaItems(convertedMediaItems);

      if (convertedMediaItems.length > 0) {
        const jobCount = newCompressionStatuses.size;
        let message = `Restored ${convertedMediaItems.length} media items from your previous session`;
        if (jobCount > 0) {
          message += ` (${jobCount} with compression history)`;
        }
        showNotification(message, 'info');
      }
    } catch (error) {
      console.warn('Failed to load media items from database:', error);
      // Don't show error to user as this is a background operation
    } finally {
      setLoadingPastMedia(false);
    }
  };

  const saveMediaItemsToDatabase = async (mediaItems: MediaItem[]) => {
    if (!idToken) {
      throw new Error('No authentication token available');
    }

    const mediaItemRequests = mediaItems.map(item => ({
      googleMediaItemId: item.id,
      filename: item.filename,
      mimeType: item.mimeType,
      baseUrl: item.baseUrl,
      mediaType: item.mimeType?.startsWith('video/') ? 1 : 0, // 0 = Photo, 1 = Video
      width: item.mediaMetadata?.width ? parseInt(item.mediaMetadata.width) : null,
      height: item.mediaMetadata?.height ? parseInt(item.mediaMetadata.height) : null,
      fileSizeBytes: item.fileSizeBytes || estimateFileSize(item),
      creationTime: item.mediaMetadata?.creationTime ? new Date(item.mediaMetadata.creationTime).toISOString() : null,
      metadata: JSON.stringify(item.mediaMetadata)
    }));

    const response = await fetch('/api/usermediaitems/batch', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(mediaItemRequests)
    });

  };

  const removeMultipleMediaItemsFromDatabase = async (mediaItemIds: string[]) => {
    if (!idToken) {
      throw new Error('No authentication token available');
    }

    // Optimized: Use the new batch endpoint that accepts Google IDs directly
    // This avoids N+1 lookups and rate limiting issues
    const response = await fetch('/api/usermediaitems/batch?idType=google', {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(mediaItemIds)
    });

    if (!response.ok) {
      throw new Error(`Failed to remove media items: ${response.status}`);
    }

    const result = await response.json();
    console.log('Successfully removed media items from database:', result);
    return result;
  };



  const handleRemoveSelectedItems = async () => {
    if (selectedItems.size === 0) {
      showNotification('No items selected', 'info');
      return;
    }

    try {
      const selectedIds = Array.from(selectedItems);
      await removeMultipleMediaItemsFromDatabase(selectedIds);

      // Remove from UI state
      setMediaItems(prevItems => prevItems.filter(item => !selectedItems.has(item.id)));
      setSelectedItems(new Set());
      setLastSelectedIndex(null);

      showNotification(`Successfully removed ${selectedIds.length} media items`, 'success');
    } catch (error) {
      console.error('Failed to remove selected media items:', error);
      showNotification('Failed to remove selected media items', 'error');
    }
  };

  const handleSelectFromGooglePhotos = async () => {
    if (!idToken) return;

    try {
      setVideosLoading(true);
      setCancelPolling(false);
      cancelPollingRef.current = false;
      console.log('Initial cancelPolling state set to false');
      console.log('Creating picker session with token:', idToken?.substring(0, 20) + '...');

      // Step 1: Create picker session
      const response = await fetch('/api/videos', {
        headers: {
          'Authorization': `Bearer ${idToken}`
        }
      });

      console.log('Response status:', response.status);
      const responseText = await response.text();
      console.log('Response text:', responseText);

      if (!response.ok) {
        const errorData = responseText ? JSON.parse(responseText) : {};

        // Handle re-authentication requirement
        if (response.status === 403 && errorData.requiresReauth) {
          showNotification('Google Photos access requires re-authentication. Redirecting...', 'info');
          setTimeout(() => {
            window.location.href = errorData.authUrl;
          }, 2000);
          return;
        }

        throw new Error(`Failed to create picker session: ${response.status} - ${responseText}`);
      }

      const sessionData = responseText ? JSON.parse(responseText) : {};
      console.log('Picker session created:', sessionData);

      if (!sessionData.pickerUri) {
        throw new Error('No picker URI received from server');
      }

      // Step 2: Open picker URI in new window/tab
      showNotification('Google Photos picker opened! Select your photos and videos, then click "Done". Use the Cancel button below if you want to stop.', 'info');
      const pickerWindow = window.open(sessionData.pickerUri, '_blank', 'width=1000,height=700');

      // Note: Window closure detection is unreliable for cross-origin redirects like Google Photos
      // Users can manually cancel using the cancel button, or the session will timeout naturally

      // Step 3: Poll for session completion
      let pollCount = 0;
      let sessionStartTime = Date.now();
      let sessionTimeoutMs = 5 * 60 * 1000; // Default 5 minutes, will be updated from pollingConfig

      const pollSession = async () => {
        try {
          // Check if user cancelled
          if (cancelPollingRef.current) {
            console.log('Polling cancelled by user');
            showNotification('Photo selection cancelled', 'info');
            if (pickerWindow && !pickerWindow.closed) {
              pickerWindow.close();
            }
            setVideosLoading(false);
            return;
          }

          pollCount++;
          console.log(`Polling session status (attempt ${pollCount})`);

          const statusResponse = await fetch(`/api/videos/session/${sessionData.sessionId}/status`, {
            headers: {
              'Authorization': `Bearer ${idToken}`
            }
          });

          if (!statusResponse.ok) {
            throw new Error(`Failed to check session status: ${statusResponse.status}`);
          }

          const statusData = await statusResponse.json();
          console.log('Session status:', statusData);

          // Log polling configuration if available
          if (statusData.pollingConfig) {
            console.log(`Google recommends: poll every ${statusData.pollingConfig.pollInterval || 'default'}, timeout in ${statusData.pollingConfig.timeoutIn || 'default'}`);
          }

          // Update timeout from Google's pollingConfig on each response (it can change)
          if (statusData.pollingConfig?.timeoutIn) {
            const timeoutSeconds = parseFloat(statusData.pollingConfig.timeoutIn.replace('s', ''));

            // If timeoutIn is 0, Google is telling us to stop polling immediately
            if (timeoutSeconds === 0) {
              console.log('Google API indicates to stop polling (timeoutIn: 0s)');
              showNotification('Photo selection session expired. Please try again.', 'error');

              setVideosLoading(false);
              return;
            }

            sessionTimeoutMs = timeoutSeconds * 1000;
            if (pollCount === 1) {
              console.log(`Session timeout set to ${timeoutSeconds} seconds`);
            }
          }

          if (statusData.mediaItemsSet) {
            // Step 4: Get selected videos
            console.log('Media items selected! Fetching videos...');
            const videosResponse = await fetch(`/api/videos/session/${sessionData.sessionId}/videos`, {
              headers: {
                'Authorization': `Bearer ${idToken}`
              }
            });

            if (!videosResponse.ok) {
              throw new Error(`Failed to fetch selected videos: ${videosResponse.status}`);
            }

            const mediaData = await videosResponse.json();
            console.log('Selected media response:', mediaData);
            console.log('Debug info:', mediaData.debug);
            console.log('Total selected items:', mediaData.totalSelected);
            console.log('Video count:', mediaData.videoCount);
            console.log('Photo count:', mediaData.photoCount);
            console.log('MIME types found:', mediaData.debug?.mimeTypes);

            const loadedMediaItems = mediaData.mediaItems || [];

            // Add estimated file sizes to loaded media items for immediate display
            const mediaItemsWithSizes = loadedMediaItems.map((item: MediaItem) => ({
              ...item,
              fileSizeBytes: item.fileSizeBytes || estimateFileSize(item)
            }));

            // Merge new items with existing ones (avoid duplicates) - put new items at the beginning
            setMediaItems(prevItems => {
              const existingIds = new Set(prevItems.map((item: MediaItem) => item.id));
              const newItems = mediaItemsWithSizes.filter((item: MediaItem) => !existingIds.has(item.id));
              return [...newItems, ...prevItems];
            });

            // Save media items to database for persistence
            if (mediaItemsWithSizes.length > 0) {
              try {
                await saveMediaItemsToDatabase(mediaItemsWithSizes);
              } catch (saveError) {
                console.warn('Failed to save media items to database:', saveError);
                // Don't show error to user as the main functionality still works
              }
            }

            const totalItems = mediaData.totalSelected || 0;
            const videoCount = mediaData.videoCount || 0;
            const photoCount = mediaData.photoCount || 0;

            if (totalItems > 0) {
              let message = `Successfully loaded ${totalItems} items from Google Photos`;
              if (videoCount > 0 && photoCount > 0) {
                message += ` (${videoCount} videos, ${photoCount} photos)`;
              } else if (videoCount > 0) {
                message += ` (${videoCount} videos)`;
              } else if (photoCount > 0) {
                message += ` (${photoCount} photos)`;
              }
              showNotification(message, 'success');
            } else {
              showNotification('No items were selected', 'info');
            }

            // Step 5: Clean up session
            try {
              await fetch(`/api/videos/session/${sessionData.sessionId}`, {
                method: 'DELETE',
                headers: {
                  'Authorization': `Bearer ${idToken}`
                }
              });
              console.log('Session cleaned up successfully');
            } catch (cleanupErr) {
              console.warn('Failed to clean up session:', cleanupErr);
            }

            if (pickerWindow && !pickerWindow.closed) {
              pickerWindow.close();
            }
            setVideosLoading(false);
          } else {
            // Check if session has timed out based on Google's timeoutIn
            const elapsedTime = Date.now() - sessionStartTime;
            if (elapsedTime >= sessionTimeoutMs) {
              console.log(`Session timed out after ${elapsedTime}ms`);
              showNotification('Photo selection timed out. Please try again.', 'error');
              if (pickerWindow && !pickerWindow.closed) {
                pickerWindow.close();
              }
              setVideosLoading(false);
              return;
            }

            // Use Google's recommended polling interval (default to 1 second for faster response)
            let pollInterval = 1000; // Default 1 second
            if (statusData.pollingConfig?.pollInterval) {
              // Parse duration format like "3.5s" or "2s"
              const pollIntervalSeconds = parseFloat(statusData.pollingConfig.pollInterval.replace('s', ''));
              pollInterval = pollIntervalSeconds * 1000;
            }

            console.log(`Continuing to poll in ${pollInterval}ms... (${Math.round((sessionTimeoutMs - elapsedTime) / 1000)}s remaining)`);
            setTimeout(pollSession, pollInterval);
          }
        } catch (pollErr) {
          console.error('Error polling session:', pollErr);

          // Check if session has timed out
          const elapsedTime = Date.now() - sessionStartTime;
          if (elapsedTime >= sessionTimeoutMs) {
            console.log('Session timed out during error');
            showNotification('Photo selection timed out. Please try again.', 'error');
            setVideosLoading(false);
            return;
          }

          // For early errors, retry more quickly but with exponential backoff
          const retryDelay = pollCount <= 3 ? Math.min(1000 * pollCount, 3000) : 2000;
          console.log(`Retrying in ${retryDelay}ms due to error (attempt ${pollCount})`);

          // Only show error notification after several failed attempts
          if (pollCount > 5) {
            showNotification('Having trouble checking selection status, but still trying...', 'info');
          }

          setTimeout(pollSession, retryDelay);
        }
      };

      // Start polling immediately after a brief delay to let the picker load
      console.log('Starting polling in 2 seconds...');
      setTimeout(pollSession, 2000);

    } catch (err) {
      console.error('Error loading videos:', err);
      showNotification(`Error loading videos: ${err instanceof Error ? err.message : 'Unknown error'}`, 'error');
      setVideosLoading(false);
    }
  };

  // Jobs panel handlers
  const handleJobsPanelToggle = () => {
    setJobsPanelCollapsed(!jobsPanelCollapsed);
  };

  // Operations tray handlers
  const handleOperationsTrayToggle = () => {
    setOperationsTrayCollapsed(!operationsTrayCollapsed);
  };

  const handleClearJob = async (jobId: string) => {
    if (!idToken) return;

    try {
      const response = await fetch(`/api/media/jobs/${jobId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${idToken}`
        }
      });

      if (response.ok) {
        // Remove job from local state
        setJobs(prev => prev.filter(job => job.jobId !== jobId));
        showNotification('Job removed', 'success');
      } else {
        showNotification('Failed to remove job', 'error');
      }
    } catch (error) {
      console.error('Error removing job:', error);
      showNotification('Error removing job', 'error');
    }
  };

  const handleClearAllJobs = async () => {
    if (!idToken || jobs.length === 0) return;

    try {
      // Check how many jobs can be deleted (completed, failed, cancelled)
      const deletableJobs = jobs.filter(job =>
        ['completed', 'failed', 'cancelled'].includes(job.status.toLowerCase())
      );

      if (deletableJobs.length === 0) {
        showNotification('No jobs can be cleared (only completed/failed jobs can be removed)', 'info');
        return;
      }

      const response = await fetch('/api/media/jobs', {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${idToken}`
        }
      });

      if (response.ok) {
        const result = await response.json();
        const deletedCount = result.deletedCount || 0;

        // Remove deleted jobs from local state
        setJobs(prev => prev.filter(job =>
          !['completed', 'failed', 'cancelled'].includes(job.status.toLowerCase())
        ));

        if (deletedCount > 0) {
          showNotification(`Cleared ${deletedCount} jobs`, 'success');
        } else {
          showNotification('No jobs were cleared', 'info');
        }
      } else {
        showNotification('Failed to clear jobs', 'error');
      }
    } catch (error) {
      console.error('Error clearing jobs:', error);
      showNotification('Error clearing jobs', 'error');
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'background.default',
        width: '100%',
        margin: 0,
        padding: 0
      }}>
        {/* Google One Tap - only show when not authenticated and not on splash page */}
        {!user && window.location.pathname !== '/splash' && (
          <GoogleOneTap
            onSuccess={handleOneTapSuccess}
            onError={handleOneTapError}
            disabled={false}
            showDebugButton={false}
          />
        )}
        <AppBar
          position="static"
          elevation={0}
          sx={{
            backgroundColor: 'grey.50',
            borderBottom: 1,
            borderColor: 'divider',
            color: 'text.primary',
            boxShadow: 'none',
            width: '100%',
            margin: 0,
            padding: 0
          }}
        >
          <Toolbar sx={{ py: 1, boxShadow: '0 4px 12px rgba(0,0,0,0.15)', px: { xs: 2, sm: 3, md: 4 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                  '&:hover': {
                    opacity: 0.8
                  }
                }}
                onClick={() => navigate('/')}
              >
                <ThemeAwareLogo
                  size={30}
                  sx={{
                    mr: { xs: 0.5, sm: 1 },
                    width: { xs: 24, sm: 30 },
                    height: { xs: 24, sm: 30 }
                  }}
                />
                <Box sx={{ display: { xs: 'none', sm: 'flex' }, flexGrow: 1, alignItems: 'flex-end' }}>
                  <Typography
                    variant="h6"
                    component="div"
                    sx={{
                      fontWeight: 300,
                      color: 'text.primary',
                      letterSpacing: '0px',
                      wordSpacing: '-2px',
                      mr: 1
                    }}
                  >
                    Gallery Tuner
                  </Typography>
                  <Typography
                    component="div"
                    sx={{
                      fontSize: '14px',
                      fontWeight: 150,
                      color: 'text.primary',
                      letterSpacing: '0px',
                      wordSpacing: '0px',
                      ml: -0.3,
                      mb: 0.7
                    }}
                  >
                    for Google Photos
                  </Typography>
                </Box>
                {/* Mobile-only compact title */}
                <Typography
                  variant="h6"
                  component="div"
                  sx={{
                    display: { xs: 'block', sm: 'none' },
                    fontWeight: 300,
                    color: 'text.primary',
                    fontSize: '1rem',
                    ml: 0.5
                  }}
                >
                  Gallery Tuner
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ThemeSelector />
              {user && (
                <AccountDropdown
                  userEmail={getCurrentUserEmail()}
                  firstName={userProfile?.firstName}
                  profilePictureUrl={userProfile?.profilePictureUrl}
                  onLogout={handleLogout}
                />
              )}
            </Box>
          </Toolbar>
        </AppBar>
        <Box sx={{ pt: 2, px: { xs: 0, sm: 3, md: 2 }, flex: 1, display: 'flex', flexDirection: 'column' }}>
          <Snackbar
            open={!!notification}
            autoHideDuration={6000}
            onClose={() => setNotification(null)}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          >
            <Alert
              onClose={() => setNotification(null)}
              severity={notificationType}
              variant="filled"
              sx={{ width: '100%' }}
            >
              {notification}
            </Alert>
          </Snackbar>
          {authLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
              <CircularProgress />
            </Box>
          ) : (
            <Routes>
              <Route path="/splash" element={
                <SplashPage
                  onSignIn={handleGoogleSignIn}
                  onOneTapSuccess={handleOneTapSuccess}
                  onOneTapError={handleOneTapError}
                />
              } />
              <Route path="/account" element={
                !user ? <Navigate to="/splash" replace /> : (
                  <AccountScreen
                    userEmail={getCurrentUserEmail()}
                    firstName={userProfile?.firstName}
                    profilePictureUrl={userProfile?.profilePictureUrl}
                    userCredits={userCredits}
                    creditsLoading={creditsLoading}
                    onLogout={handleLogout}
                    token={idToken}
                    notificationsEnabled={notificationsEnabled}
                    onNotificationsChange={handleNotificationsChange}
                  />
                )
              } />
              <Route path="/" element={
                !user ? <Navigate to="/splash" replace /> : (
                  <Fade in timeout={800}>
                    <Box>

                      {user && mediaItems.length > 0 && (
                        <Paper
                          elevation={0}
                          sx={{
                            p: 3,
                            mb: 2,
                            backgroundColor: 'grey.50',
                            border: 1,
                            borderColor: 'divider',
                            borderRadius: 1,
                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                          }}
                        >
                          <Box sx={{
                            display: 'flex',
                            flexDirection: { xs: 'column', md: 'row' },
                            justifyContent: { xs: 'flex-start', md: 'space-between' },
                            alignItems: { xs: 'stretch', md: 'center' },
                            flexWrap: 'wrap',
                            gap: 2
                          }}>
                            {/* Left side: Load from Google Photos and Subscribe buttons */}
                            <Box sx={{
                              display: 'flex',
                              gap: 2,
                              alignItems: 'center',
                              flexWrap: 'wrap',
                              width: { xs: '100%', md: 'auto' },
                              flexDirection: { xs: 'column', md: 'row' }
                            }}>
                              {videosLoading ? (
                                <Button
                                  variant="contained"
                                  size="large"
                                  disabled={true}
                                  sx={{
                                    backgroundColor: 'action.disabledBackground',
                                    color: 'action.disabled',
                                    textTransform: 'none',
                                    fontWeight: 500,
                                    borderRadius: 1,
                                    px: 3,
                                    py: 1.5,
                                    width: { xs: '100%', md: 'auto' }
                                  }}
                                >
                                  Waiting for media selection...
                                </Button>
                              ) : (
                                <GooglePhotosButton
                                  onClick={handleSelectFromGooglePhotos}
                                  size="large"
                                  fullWidth={false}
                                  sx={{ width: { xs: '100%', md: 'auto' } }}
                                />
                              )}
                              {videosLoading && (
                                <Button
                                  variant="outlined"
                                  size="large"
                                  onClick={() => {
                                    setCancelPolling(true);
                                    cancelPollingRef.current = true;
                                  }}
                                  sx={{
                                    borderColor: 'error.main',
                                    color: 'error.main',
                                    textTransform: 'none',
                                    fontWeight: 500,
                                    '&:hover': {
                                      borderColor: 'error.dark',
                                      backgroundColor: 'error.light',
                                      color: 'error.dark'
                                    },
                                    borderRadius: 1,
                                    px: 3,
                                    py: 1.5
                                  }}
                                >
                                  Cancel
                                </Button>
                              )}
                              {/* Credits Display */}
                              <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                px: 1,
                                py: 0.75,
                                bgcolor: (!creditsLoading && userCredits < 10) ? 'warning.light' : 'grey.100',
                                borderRadius: 1,
                                border: '1px solid',
                                borderColor: (!creditsLoading && userCredits < 10) ? 'warning.main' : 'grey.200',
                                width: { xs: '100%', md: 'auto' },
                                justifyContent: { xs: 'space-between', md: 'flex-start' }
                              }}>
                                <Compress sx={{
                                  fontSize: 18,
                                  color: (!creditsLoading && userCredits < 10) ? 'warning.dark' : 'text.primary'
                                }} />
                                <Typography
                                  variant="body1"
                                  component="span"
                                  sx={{
                                    fontWeight: 500,
                                    color: (!creditsLoading && userCredits < 10) ? 'warning.dark' : 'text.primary',
                                    whiteSpace: 'nowrap'
                                  }}
                                >
                                  {creditsLoading ? (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      <CircularProgress size={14} />
                                      <span>Loading...</span>
                                    </Box>
                                  ) : (
                                    `${userCredits} credits`
                                  )}
                                </Typography>
                                <Button
                                  variant={(!creditsLoading && userCredits < 10) ? "contained" : "outlined"}
                                  color={(!creditsLoading && userCredits < 10) ? "warning" : "primary"}
                                  sx={{
                                    minWidth: 'auto',
                                    textTransform: 'none',
                                    fontWeight: 400,
                                    borderRadius: 1,
                                    width: { xs: '100%', md: 'auto' }
                                  }}
                                  onClick={() => {
                                    navigate('/add-credits');
                                  }}
                                >
                                  {(!creditsLoading && userCredits < 10) ? 'Buy More' : 'Add Credits'}
                                </Button>
                              </Box>
                            </Box>

                            {/* Right side: Sorting controls and item count */}
                            <Box sx={{
                              display: 'flex',
                              gap: 2,
                              alignItems: 'center',
                              flexWrap: 'nowrap',
                              width: { xs: '100%', md: 'auto' },
                              justifyContent: { xs: 'flex-start', md: 'flex-end' }
                            }}>
                              <GallerySortControls
                                sortBy={sortBy}
                                onSortChange={setSortBy}
                                totalItems={mediaItems.length}
                                sx={{ width: { xs: '100%', md: 'auto' } }}
                              />
                            </Box>
                          </Box>
                        </Paper>
                      )}

                      {/* Toolbar for when no media items are loaded */}
                      {user && mediaItems.length === 0 && (
                        <Paper
                          elevation={0}
                          sx={{
                            p: 3,
                            mb: 2,
                            backgroundColor: 'grey.50',
                            border: 1,
                            borderColor: 'divider',
                            borderRadius: 1,
                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                          }}
                        >
                          <Box sx={{
                            display: 'flex',
                            flexDirection: { xs: 'column', md: 'row' },
                            justifyContent: { xs: 'flex-start', md: 'space-between' },
                            alignItems: { xs: 'stretch', md: 'center' },
                            flexWrap: 'wrap',
                            gap: 2
                          }}>
                            {/* Left side: Load from Google Photos button and Credits Display */}
                            <Box sx={{
                              display: 'flex',
                              gap: 2,
                              alignItems: 'center',
                              flexWrap: 'wrap',
                              width: { xs: '100%', md: 'auto' },
                              flexDirection: { xs: 'column', md: 'row' }
                            }}>
                              {videosLoading ? (
                                <Button
                                  variant="contained"
                                  size="large"
                                  disabled={true}
                                  sx={{
                                    backgroundColor: 'action.disabledBackground',
                                    color: 'action.disabled',
                                    textTransform: 'none',
                                    fontWeight: 500,
                                    borderRadius: 1,
                                    px: 3,
                                    py: 1.5,
                                    width: { xs: '100%', md: 'auto' }
                                  }}
                                >
                                  Waiting for media selection...
                                </Button>
                              ) : (
                                <GooglePhotosButton
                                  onClick={handleSelectFromGooglePhotos}
                                  size="large"
                                  sx={{ width: { xs: '100%', md: 'auto' } }}
                                />
                              )}
                              {videosLoading && (
                                <Button
                                  variant="outlined"
                                  size="large"
                                  onClick={() => {
                                    setCancelPolling(true);
                                    cancelPollingRef.current = true;
                                  }}
                                  sx={{
                                    borderColor: 'error.main',
                                    color: 'error.main',
                                    textTransform: 'none',
                                    fontWeight: 500,
                                    '&:hover': {
                                      borderColor: 'error.dark',
                                      backgroundColor: 'error.light',
                                      color: 'error.dark'
                                    },
                                    borderRadius: 1,
                                    px: 3,
                                    py: 1.5,
                                    width: { xs: '100%', md: 'auto' }
                                  }}
                                >
                                  Cancel
                                </Button>
                              )}
                              {/* Credits Display */}
                              <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                px: 3,
                                py: 0.75,
                                bgcolor: (!creditsLoading && userCredits < 10) ? 'warning.light' : 'grey.100',
                                borderRadius: 1,
                                border: '1px solid',
                                borderColor: (!creditsLoading && userCredits < 10) ? 'warning.main' : 'grey.300',
                                width: { xs: '100%', md: 'auto' },
                                justifyContent: { xs: 'space-between', md: 'flex-start' }
                              }}>
                                <Compress sx={{
                                  fontSize: 18,
                                  color: (!creditsLoading && userCredits < 10) ? 'warning.dark' : 'text.primary'
                                }} />
                                <Typography
                                  variant="body1"
                                  component="span"
                                  sx={{
                                    fontWeight: 600,
                                    color: (!creditsLoading && userCredits < 10) ? 'warning.dark' : 'text.primary',
                                    whiteSpace: 'nowrap'
                                  }}
                                >
                                  {creditsLoading ? (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      <CircularProgress size={14} />
                                      <span>Loading...</span>
                                    </Box>
                                  ) : (
                                    `${userCredits} credits`
                                  )}
                                </Typography>
                                <Button
                                  variant={(!creditsLoading && userCredits < 10) ? "contained" : "outlined"}
                                  color={(!creditsLoading && userCredits < 10) ? "warning" : "primary"}
                                  sx={{
                                    minWidth: 'auto',
                                    textTransform: 'none',
                                    fontWeight: 500,
                                    borderRadius: 1,
                                    width: { xs: '100%', md: 'auto' }
                                  }}
                                  onClick={() => {
                                    navigate('/add-credits');
                                  }}
                                >
                                  {(!creditsLoading && userCredits < 10) ? 'Buy More' : 'Add Credits'}
                                </Button>
                              </Box>
                            </Box>

                            {/* Right side: Sorting controls (disabled when no items) */}
                            <Box sx={{
                              display: 'flex',
                              gap: 2,
                              alignItems: 'center',
                              flexWrap: 'nowrap',
                              width: { xs: '100%', md: 'auto' },
                              justifyContent: { xs: 'flex-start', md: 'flex-end' }
                            }}>
                              <GallerySortControls
                                sortBy={sortBy}
                                onSortChange={setSortBy}
                                totalItems={0}
                                disabled={true}
                                sx={{ width: { xs: '100%', md: 'auto' } }}
                              />
                            </Box>
                          </Box>
                        </Paper>
                      )}

                      {user ? (
                        <>
                          {/* Main Content Area with Sidebar Layout */}
                          <Box sx={{
                            display: 'flex',
                            gap: { xs: 2, md: 2 },
                            alignItems: 'flex-start',
                            width: '100%',
                            flexDirection: { xs: 'column', md: 'row' },
                            pb: {
                              xs: !jobsPanelCollapsed || !operationsTrayCollapsed
                                ? '72vh' // Either tray expanded
                                : '80px', // Both trays collapsed (56px height + 12px margin + 12px breathing room)
                              sm: !jobsPanelCollapsed || !operationsTrayCollapsed
                                ? '62vh' // Either tray expanded
                                : '80px', // Both trays collapsed
                              md: 3 // Desktop - no trays affecting layout
                            }
                          }}>
                            {/* Operations Sidebar - Desktop Only */}
                            <Box sx={{
                              order: { xs: 2, md: 1 },
                              width: { xs: '100%', md: 'auto' },
                              display: { xs: 'none', md: 'block' } // Hide on mobile
                            }}>
                              <OperationsSidebar
                                selectedItems={selectedItems}
                                totalItems={mediaItems.length}
                                expiredItemsCount={expiredMediaItems.size}
                                onSelectAll={handleSelectAll}
                                onClearSelection={handleClearSelection}
                                onCompressSelected={handleBatchCompress}
                                onRemoveSelected={handleRemoveSelectedItems}
                                onClearExpired={handleClearAllExpired}
                                isCollapsed={sidebarCollapsed}
                                onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
                              />
                            </Box>

                            {/* Content Area */}
                            <Box sx={{
                              flex: 1,
                              minWidth: 0,
                              width: '100%',
                              maxWidth: '100%',
                              order: { xs: 1, md: 2 }
                            }}>
                              {loadingPastMedia ? (
                                <Paper
                                  elevation={0}
                                  sx={{
                                    p: 6,
                                    textAlign: 'center',
                                    backgroundColor: 'grey.50',
                                    border: 1,
                                    borderColor: 'divider',
                                    borderRadius: 1
                                  }}
                                >
                                  <CircularProgress sx={{ mb: 2 }} />
                                  <Typography variant="h5" gutterBottom sx={{ color: 'text.primary', fontWeight: 400 }}>
                                    Loading your media
                                  </Typography>
                                  <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                                    Restoring media from previous sessions...
                                  </Typography>
                                </Paper>
                              ) : mediaItems.length > 0 ? (
                                <>
                                  {/* Instructions */}
                                  {showInstructions && (
                                    <Box sx={{
                                      mb: 3,
                                      textAlign: 'center',
                                      position: 'relative',
                                      backgroundColor: 'grey.50',
                                      borderRadius: 1,
                                      p: 2,
                                      border: 1,
                                      borderColor: 'divider'
                                    }}>
                                      <IconButton
                                        size="small"
                                        onClick={() => setShowInstructions(false)}
                                        sx={{
                                          position: 'absolute',
                                          top: '50%',
                                          right: 8,
                                          transform: 'translateY(-50%)',
                                          color: 'text.secondary'
                                        }}
                                      >
                                        <Close fontSize="small" />
                                      </IconButton>
                                      <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '14px', pr: 4 }}>
                                        Click to select items • Shift+click to select range • Ctrl/Cmd+click to toggle selection
                                      </Typography>
                                    </Box>
                                  )}

                                  <Box
                                    sx={{
                                      display: 'grid',
                                      gridTemplateColumns: {
                                        xs: 'repeat(auto-fill, minmax(150px, 1fr))',
                                        sm: 'repeat(auto-fill, minmax(180px, 1fr))',
                                        md: 'repeat(auto-fill, minmax(200px, 1fr))',
                                        lg: 'repeat(auto-fill, minmax(200px, 1fr))'
                                      },
                                      gap: 0,
                                      width: '100%',
                                      userSelect: 'none',
                                      WebkitUserSelect: 'none',
                                      MozUserSelect: 'none',
                                      msUserSelect: 'none'
                                    }}
                                  >
                                    {sortedMediaItems.map((mediaItem, index) => {
                                      const isSelected = selectedItems.has(mediaItem.id);
                                      const isExpired = expiredMediaItems.has(mediaItem.id);
                                      return (
                                        <Box
                                          key={mediaItem.id || index}
                                          onClick={(e) => handleItemClick(mediaItem, index, e)}
                                          sx={{
                                            position: 'relative',
                                            aspectRatio: '1',
                                            overflow: 'hidden',
                                            cursor: 'pointer',
                                            border: isSelected ? '3px solid' : isExpired ? '3px solid' : '3px solid transparent',
                                            borderColor: isSelected ? 'primary.main' : isExpired ? 'warning.main' : 'transparent',
                                            borderRadius: '7px',
                                            transition: 'border-color 0.2s ease',
                                            opacity: isExpired ? 0.7 : 1,
                                            userSelect: 'none',
                                            WebkitUserSelect: 'none',
                                            MozUserSelect: 'none',
                                            msUserSelect: 'none',
                                            '&:hover .media-overlay': {
                                              opacity: 1
                                            },
                                            '&:hover .media-image': {
                                              transform: 'scale(1.05)'
                                            },
                                            '&:hover .selection-indicator': {
                                              opacity: 1
                                            }
                                          }}
                                        >
                                          {/* Media Image */}
                                          {mediaItem.id && idToken && !isMediaItemExpired(mediaItem) ? (
                                            <img
                                              className="media-image"
                                              src={`/api/videos/${mediaItem.id}/preview?width=400&height=400&crop=true&token=${encodeURIComponent(idToken)}&baseUrl=${encodeURIComponent(mediaItem.baseUrl)}`}
                                              alt={mediaItem.filename}
                                              style={{
                                                width: '100%',
                                                height: '100%',
                                                objectFit: 'cover',
                                                borderRadius: '4px',
                                                transition: 'transform 0.2s ease, opacity 0.3s ease',
                                                display: 'block',
                                                opacity: 0
                                              }}
                                              onLoad={(e) => {
                                                // Fade in the image once it's loaded
                                                const target = e.currentTarget;
                                                target.style.opacity = '1';
                                              }}
                                              onError={(e) => {
                                                // Mark this media item as expired and show appropriate message
                                                handleMediaExpired(mediaItem.id);

                                                const target = e.currentTarget;
                                                target.style.display = 'none';
                                                const parent = target.parentElement!;
                                                const fallbackDiv = document.createElement('div');
                                                fallbackDiv.className = 'media-image';
                                                fallbackDiv.style.cssText = `
                                    width: 100%;
                                    height: 100%;
                                    display: flex;
                                    flex-direction: column;
                                    align-items: center;
                                    justify-content: center;
                                    background-color: #fff3cd;
                                    color: #856404;
                                    border: 2px solid #ffeaa7;
                                    transition: transform 0.2s ease, opacity 0.3s ease;
                                    opacity: 0;
                                  `;
                                                fallbackDiv.innerHTML = `
                                    <span style="font-size: 32px;">⚠️</span>
                                    <span style="margin-top: 8px; font-size: 11px; text-align: center; font-weight: 500;">Access Expired</span>
                                    <span style="margin-top: 2px; font-size: 10px; text-align: center;">Google Photos URL expired</span>
                                  `;
                                                parent.appendChild(fallbackDiv);
                                                // Fade in the fallback
                                                setTimeout(() => {
                                                  fallbackDiv.style.opacity = '1';
                                                }, 10);
                                              }}
                                            />
                                          ) : (
                                            <Fade in={true} timeout={300}>
                                              <Box
                                                className="media-image"
                                                sx={{
                                                  width: '100%',
                                                  height: '100%',
                                                  display: 'flex',
                                                  flexDirection: 'column',
                                                  alignItems: 'center',
                                                  justifyContent: 'center',
                                                  backgroundColor: isMediaItemExpired(mediaItem) ? '#fff3cd' : 'grey.100',
                                                  color: isMediaItemExpired(mediaItem) ? '#856404' : 'text.secondary',
                                                  border: isMediaItemExpired(mediaItem) ? '2px solid #ffeaa7' : 'none',
                                                  transition: 'transform 0.2s ease'
                                                }}
                                              >
                                                {isMediaItemExpired(mediaItem) ? (
                                                  <>
                                                    <Box sx={{ fontSize: '32px' }}>⚠️</Box>
                                                    <Typography variant="body2" sx={{ fontSize: '11px', fontWeight: 500, mt: 1, textAlign: 'center' }}>
                                                      Access Expired
                                                    </Typography>
                                                    <Typography variant="caption" sx={{ fontSize: '10px', textAlign: 'center' }}>
                                                      Google Photos URL expired
                                                    </Typography>
                                                  </>
                                                ) : (
                                                  <>
                                                    <Box sx={{ fontSize: '48px', mb: 1 }}>
                                                      {mediaItem.mimeType?.startsWith('video/') ? '🎥' : '📷'}
                                                    </Box>
                                                    <Typography variant="body2" sx={{ fontSize: '12px' }}>
                                                      Preview unavailable
                                                    </Typography>
                                                  </>
                                                )}
                                              </Box>
                                            </Fade>
                                          )}

                                          {/* Video Duration Badge */}
                                          {mediaItem.mimeType?.startsWith('video/') && (
                                            <Box
                                              sx={{
                                                position: 'absolute',
                                                bottom: 8,
                                                right: 8,
                                                backgroundColor: 'rgba(0, 0, 0, 0.7)',
                                                color: 'white',
                                                padding: '2px 6px',
                                                borderRadius: '4px',
                                                fontSize: '12px',
                                                fontWeight: 500,
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 0.5
                                              }}
                                            >
                                              Video
                                            </Box>
                                          )}

                                          {/* Compression Status Indicator */}
                                          <CompressionStatusIndicator
                                            mediaItemId={mediaItem.id}
                                            status={compressionStatuses.get(mediaItem.id)}
                                            showOnlyLoadingCircle={true}
                                          />



                                          {/* Selection Indicator */}
                                          <Box
                                            className="selection-indicator"
                                            sx={{
                                              position: 'absolute',
                                              top: 8,
                                              right: 8,
                                              width: 24,
                                              height: 24,
                                              display: 'flex',
                                              alignItems: 'center',
                                              justifyContent: 'center',
                                              zIndex: 2,
                                              opacity: isSelected ? 1 : 0,
                                              transition: 'opacity 0.2s ease'
                                            }}
                                          >
                                            {isSelected ? (
                                              <CheckCircle
                                                sx={{
                                                  fontSize: 24,
                                                  color: 'primary.main',
                                                  backgroundColor: 'background.paper',
                                                  borderRadius: '50%'
                                                }}
                                              />
                                            ) : (
                                              <CheckCircleOutline
                                                sx={{
                                                  fontSize: 24,
                                                  color: 'white',
                                                  filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.5))'
                                                }}
                                              />
                                            )}
                                          </Box>

                                          {/* Hover Overlay */}
                                          <Box
                                            className="media-overlay"
                                            sx={{
                                              position: 'absolute',
                                              top: 0,
                                              left: 0,
                                              right: 0,
                                              bottom: 0,
                                              background: 'linear-gradient(to bottom, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0) 30%, rgba(0,0,0,0) 70%, rgba(0,0,0,0.5) 100%)',
                                              opacity: isSelected ? 0.7 : 0,
                                              transition: 'opacity 0.2s ease',
                                              display: 'flex',
                                              flexDirection: 'column',
                                              justifyContent: 'space-between',
                                              padding: 1
                                            }}
                                          >
                                            {/* Top Info */}
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                                {expiredMediaItems.has(mediaItem.id) && (
                                                  <Chip
                                                    label="Expired"
                                                    size="small"
                                                    sx={{
                                                      backgroundColor: 'warning.main',
                                                      color: 'warning.contrastText',
                                                      fontWeight: 600,
                                                      fontSize: '9px',
                                                      height: 18,
                                                      '& .MuiChip-label': {
                                                        px: 0.5
                                                      }
                                                    }}
                                                  />
                                                )}
                                                <Chip
                                                  label={mediaItem.mimeType?.startsWith('video/') ? 'Video' : 'Photo'}
                                                  size="small"
                                                  sx={{
                                                    backgroundColor: 'background.paper',
                                                    color: mediaItem.mimeType?.startsWith('video/') ? 'primary.main' : 'success.main',
                                                    fontWeight: 500,
                                                    fontSize: '10px',
                                                    height: 20,
                                                    border: 1,
                                                    borderColor: 'divider'
                                                  }}
                                                />
                                                {mediaItem.fileSizeBytes && (
                                                  <Chip
                                                    label={formatFileSize(mediaItem.fileSizeBytes)}
                                                    size="small"
                                                    sx={{
                                                      backgroundColor: 'background.paper',
                                                      color: 'text.secondary',
                                                      fontSize: '10px',
                                                      height: 20,
                                                      border: 1,
                                                      borderColor: 'divider'
                                                    }}
                                                  />
                                                )}
                                              </Box>
                                            </Box>

                                            {/* Bottom Info - Removed filename display */}
                                          </Box>
                                        </Box>
                                      );
                                    })}
                                  </Box>
                                </>
                              ) : (
                                <Paper
                                  elevation={0}
                                  sx={{
                                    p: 6,
                                    textAlign: 'center',
                                    backgroundColor: 'grey.50',
                                    border: 1,
                                    borderColor: 'divider',
                                    borderRadius: 1
                                  }}
                                >
                                  <VideoLibrary sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                                  <Typography variant="h5" gutterBottom sx={{ color: 'text.primary', fontWeight: 400 }}>
                                    No media found
                                  </Typography>
                                  <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                                    Click "Load from Google Photos" to get started
                                  </Typography>
                                </Paper>
                              )}
                            </Box>


                          </Box>
                        </>
                      ) : (
                        <Paper
                          elevation={0}
                          sx={{
                            p: 8,
                            textAlign: 'center',
                            backgroundColor: 'grey.50',
                            border: 1,
                            borderColor: 'divider',
                            borderRadius: 1
                          }}
                        >
                          <Box sx={{ mb: 3 }}>
                            <Box sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: 80,
                              height: 80,
                              borderRadius: '50%',
                              backgroundColor: 'grey.100',
                              mb: 2
                            }}>
                              <AccountCircle sx={{ fontSize: 48, color: 'text.secondary' }} />
                            </Box>
                          </Box>
                          <Typography variant="h4" gutterBottom sx={{ fontWeight: 400, color: 'text.primary' }}>
                            Welcome to Gallery Tuner
                          </Typography>
                          <Typography variant="body1" sx={{ color: 'text.secondary', mb: 4, maxWidth: 400, mx: 'auto', lineHeight: 1.6 }}>
                            Sign in with your Google account to access and adjust your media from Google Photos
                          </Typography>
                          <GoogleSignInButton
                            onSuccess={handleGoogleSignIn}
                            onError={(error) => {
                              console.error('Google Sign-In error:', error);
                              showNotification('Sign-in failxed', 'error');
                            }}
                            size="large"
                            theme="outline"
                            text="signin_with"
                            width={300}
                          />
                        </Paper>
                      )}

                      {/* Mobile Trays - Fixed at bottom when authenticated */}
                      {user && (
                        <>
                          {/* Operations Tray - Bottom Left (Mobile Only) */}
                          <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                            <OperationsTray
                              isCollapsed={operationsTrayCollapsed}
                              onToggleCollapse={handleOperationsTrayToggle}
                              selectedItems={selectedItems}
                              totalItems={mediaItems.length}
                              expiredItemsCount={expiredMediaItems.size}
                              onSelectAll={handleSelectAll}
                              onClearSelection={handleClearSelection}
                              onCompressSelected={handleBatchCompress}
                              onRemoveSelected={handleRemoveSelectedItems}
                              onClearExpired={handleClearAllExpired}
                            />
                          </Box>

                          {/* Jobs Tray - Bottom Right */}
                          <JobsTray
                            isCollapsed={jobsPanelCollapsed}
                            onToggleCollapse={handleJobsPanelToggle}
                            jobs={jobs}
                            onClearJob={handleClearJob}
                            onClearAllJobs={handleClearAllJobs}
                            mediaItems={mediaItems}
                            token={idToken}
                            notificationsEnabled={notificationsEnabled}
                            onNotificationsChange={handleNotificationsChange}
                          />

                          {/* Mobile Selection Controls - Above Trays */}
                          <MobileSelectionControls
                            selectedItems={selectedItems}
                            totalItems={mediaItems.length}
                            onSelectAll={handleSelectAll}
                            onClearSelection={handleClearSelection}
                            show={isMobile && selectedItems.size > 0}
                          />
                        </>
                      )}
                    </Box>
                  </Fade>
                )
              } />
              <Route path="/auth/callback" element={<AuthCallback />} />
              <Route path="/success" element={
                <SuccessPage onCreditsUpdated={fetchUserCredits} />
              } />
              <Route path="/cancel" element={
                <Paper
                  elevation={0}
                  sx={{
                    p: 6,
                    textAlign: 'center',
                    backgroundColor: 'background.paper',
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1
                  }}
                >
                  <Typography variant="h4" sx={{ color: 'error.main', fontWeight: 400 }} gutterBottom>
                    Subscription canceled
                  </Typography>
                  <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                    You can subscribe again anytime to access premium features.
                  </Typography>
                </Paper>
              } />
              <Route path="/add-credits" element={
                !user ? <Navigate to="/splash" replace /> : (
                  <AddCreditsPage
                    userCredits={userCredits}
                    onCreditsUpdated={fetchUserCredits}
                  />
                )
              } />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/terms-of-service" element={<TermsAndConditions />} />
              <Route path="/cookie-policy" element={<CookiePolicy />} />
            </Routes>
          )}
        </Box>

        {/* Footer - only show when not authenticated */}
        {!user && <Footer />}
      </Box>

      {/* Compression Settings Modal */}
      <Dialog
        open={compressionModalOpen}
        onClose={handleCancelCompression}
        maxWidth="sm"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: 3,
            p: 2
          }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h5" component="div" sx={{ fontWeight: 500 }}>
            Compression Settings
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {compressionTarget === 'batch'
              ? `Configure settings for ${selectedItems.size} selected items`
              : `Configure settings for ${compressionTarget?.filename || 'this media item'}`
            }
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500 }}>
              Quality Settings
            </Typography>
            <FormControl fullWidth>
              <InputLabel>Compression Quality</InputLabel>
              <Select
                value={compressionSettings.quality}
                label="Compression Quality"
                onChange={(e) => setCompressionSettings(prev => ({ ...prev, quality: e.target.value }))}
              >
                <MenuItem value="high">
                  <Box>
                    <Typography variant="body1">High Quality</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Better quality, larger file size
                    </Typography>
                  </Box>
                </MenuItem>
                <MenuItem value="medium">
                  <Box>
                    <Typography variant="body1">Medium Quality</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Balanced quality and size
                    </Typography>
                  </Box>
                </MenuItem>
                <MenuItem value="low">
                  <Box>
                    <Typography variant="body1">Low Quality</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Smaller file size, lower quality
                    </Typography>
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>
          </Box>

          {/* Credit Cost Preview */}
          <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Compress sx={{ fontSize: 20 }} />
              Credit Cost
            </Typography>
            {creditEstimate ? (
              <>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    {creditEstimate.description}
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
                    {creditEstimate.estimatedCredits} credits
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    Your balance:
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 500,
                      color: userCredits >= creditEstimate.estimatedCredits ? 'success.main' : 'error.main'
                    }}
                  >
                    {userCredits} credits
                  </Typography>
                </Box>
                {userCredits < creditEstimate.estimatedCredits && (
                  <Typography variant="caption" color="error.main" sx={{ mt: 1, display: 'block' }}>
                    Insufficient credits. You need {creditEstimate.estimatedCredits - userCredits} more credits.
                  </Typography>
                )}
              </>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
                <CircularProgress size={16} />
                <Typography variant="body2" color="text.secondary">
                  Calculating credit cost...
                </Typography>
              </Box>
            )}
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500 }}>
              Options
            </Typography>

            {/* Upload to Google Photos Option */}
            <FormControlLabel
              control={
                <Checkbox
                  checked={compressionSettings.uploadToGooglePhotos}
                  onChange={(e) => setCompressionSettings(prev => ({
                    ...prev,
                    uploadToGooglePhotos: e.target.checked
                  }))}
                />
              }
              label={
                <Box>
                  <Typography variant="body1">Upload compressed media to Google Photos</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Save the compressed media back to your Google Photos library
                  </Typography>
                </Box>
              }
              sx={{ mb: 2 }}
            />
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCancelCompression} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleConfirmCompression}
            variant="contained"
            disabled={!creditEstimate || (creditEstimate && userCredits < creditEstimate.estimatedCredits)}
            sx={{
              borderRadius: 1,
              textTransform: 'none',
              fontWeight: 500
            }}
          >
            {!creditEstimate
              ? 'Calculating...'
              : creditEstimate && userCredits < creditEstimate.estimatedCredits
                ? 'Insufficient Credits'
                : 'Start Compression'
            }
          </Button>
        </DialogActions>
      </Dialog>

    </ThemeProvider>
  );
};

// AppContent component that provides Router context
const AppContent: React.FC = () => {
  return (
    <Router>
      <AppWithNavigation />
    </Router>
  );
};

// Main App component with theme provider
const App: React.FC = () => {
  return (
    <CustomThemeProvider>
      <AppContent />
    </CustomThemeProvider>
  );
};

export default App;
